import React, { useEffect, useMemo, useState } from 'react';
import { ArrowBackIosNew, CancelRounded, CheckCircleRounded, SwapHorizRounded } from '@mui/icons-material';
import { Button, Container, Drawer, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { theme } from '../../../theme';
import { useUpdateItemReplacementRecommendationMutation } from '../../../api/physicianDashboardSlice';
import { AnimatePresence, motion } from 'framer-motion';
import ItemReplacementActions from './ItemReplacementActions';
import ItemReplacementComparison from './ItemReplacementComparison';
import { getReplacementCards, getSelectedRecommendationIds, getTotalSavings } from './itemReplacementUtils';
import ReplacementCardsSection from './ReplacementCardsSection';
import ComplianceDeclineFlow from '../ItemSwaps/Compliance/DeclineFlow/ComplianceDeclineFlow';

function ItemReplacementDetails({ replacement, cardOwnerId, draft, onBack, navigationAction }) {
    const navigate = useNavigate();
    const [updateRecommendation, { isLoading: isUpdating }] = useUpdateItemReplacementRecommendationMutation();
    const [recommendations, setRecommendations] = useState(replacement?.recommendations ?? []);
    useEffect(() => {
        setRecommendations(replacement?.recommendations ?? []);
    }, [replacement]);
    const cards = useMemo(() => getReplacementCards(recommendations), [recommendations]);
    const selectedRecommendationIds = useMemo(() => getSelectedRecommendationIds(replacement, recommendations), [recommendations, replacement]);

    const [selectedIds, setSelectedIds] = useState(selectedRecommendationIds);
    const [status, setStatus] = useState(replacement?.replacement_status ?? 'open');
    const [isDeclining, setIsDeclining] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    useEffect(() => {
        setSelectedIds(getSelectedRecommendationIds(replacement, replacement?.recommendations ?? []));
        setStatus(replacement?.replacement_status ?? 'open');
    }, [replacement]);
    const totalSavings = useMemo(() => getTotalSavings(recommendations, selectedIds), [recommendations, selectedIds]);
    const currentItem = replacement?.item ?? {};
    const proposedItem = replacement?.recommended_item ?? {};
    const reviewStatus = status === 'accepted' ? {
        label: 'Replacement accepted',
        color: theme.palette.itemSwapChip.text.positive,
        backgroundColor: theme.palette.itemSwapChip.background.positive,
        icon: <CheckCircleRounded />
    } : status === 'rejected' ? {
        label: 'Replacement rejected',
        color: theme.palette.itemSwapChip.text.negative,
        backgroundColor: theme.palette.itemSwapChip.background.negative,
        icon: <CancelRounded />
    } : status === 'partially_reviewed' ? {
        label: 'Replacement partially accepted',
        color: theme.palette.yellow.inProgress,
        backgroundColor: theme.palette.background.darkYellow,
        icon: <SwapHorizRounded />
    } : null;

    // ComplianceDeclineFlow expects swap-shaped recommendations with item/recommended_item/rejection_context
    const declineGroup = useMemo(() => ({
        recommendations: recommendations.map((recommendation) => ({
            ...recommendation,
            item: currentItem,
            recommended_item: proposedItem,
            rejection_context: recommendation.rejection_context ?? {}
        }))
    }), [recommendations, currentItem, proposedItem]);

    function toggleRecommendation(recommendationId) {
        setSelectedIds((currentIds) => currentIds.includes(recommendationId) ? currentIds.filter((id) => id !== recommendationId) : [...currentIds, recommendationId]);
    }

    async function updateRecommendations(recommendationIds, newStatus, rejectionContext = []) {
        await Promise.all(recommendationIds.map((recommendationId) => updateRecommendation({
            cardOwnerId,
            draft,
            recommendationId,
            status: newStatus,
            rejectionContext
        }).unwrap()));

        setRecommendations((currentRecommendations) => currentRecommendations.map((recommendation) => (
            recommendationIds.includes(recommendation.id)
                ? { ...recommendation, status: newStatus }
                : recommendation
        )));
    }

    async function acceptSelected(rejectionContext = []) {
        const acceptedIds = recommendations.filter(({ id }) => selectedIds.includes(id)).map(({ id }) => id);
        const rejectedIds = recommendations.filter(({ id }) => !selectedIds.includes(id)).map(({ id }) => id);

        await Promise.all([
            updateRecommendations(acceptedIds, 'accepted'),
            updateRecommendations(rejectedIds, 'rejected', rejectionContext)
        ]);
        setStatus(rejectedIds.length === 0 ? 'accepted' : 'partially_reviewed');
    }

    async function declineAll(rejectionContext = []) {
        const allIds = recommendations.map(({ id }) => id);
        await updateRecommendations(allIds, 'rejected', rejectionContext);
        setSelectedIds([]);
        setStatus('rejected');
    }

    async function undoReview() {
        const allIds = recommendations.map(({ id }) => id);
        await updateRecommendations(allIds, 'open');
        setSelectedIds(allIds);
        setStatus('open');
    }

    // Declining (fully or by omission) always requires feedback before the rejection is finalized.
    function beginDeclineFlow(action) {
        setPendingAction(action);
        setIsDeclining(true);
        window.scrollTo(0, 0);
    }

    function handleReplacementAction(action) {
        if (action === 'accept') {
            const isPartialAccept = selectedIds.length < recommendations.length;
            return isPartialAccept ? beginDeclineFlow('accept') : acceptSelected();
        }

        if (action === 'decline') {
            return beginDeclineFlow('decline');
        }

        if (action === 'undo') {
            return undoReview();
        }
    }

    async function handleDeclineFlowSubmit(rejectionContext) {
        if (pendingAction === 'accept') {
            await acceptSelected(rejectionContext);
        } else if (pendingAction === 'decline') {
            await declineAll(rejectionContext);
        }
        setPendingAction(null);
    }

    const replacementTransition = {
        enter: { x: '12%', opacity: 0 },
        center: { x: 0, opacity: 1 },
        exit: { x: '-12%', opacity: 0 }
    };

    return (
        <Drawer
            anchor='right'
            open={Boolean(replacement)}
            onClose={onBack}
            PaperProps={{ sx: { width: { xs: '100%', md: '50%' }, height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: theme.palette.background.itemSwap } }}
        >
            <Stack sx={{ height: '100dvh', minHeight: 0, overflow: 'hidden', backgroundColor: theme.palette.background.itemSwap }}>
                {!isDeclining &&
                    <Stack direction='row' alignItems='center' spacing={1} px={2} py={1.75} sx={{ backgroundColor: theme.palette.background.white, boxShadow: 1 }}>
                        <Button aria-label='Back' onClick={onBack ?? (() => navigate(-1))} sx={{ minWidth: 32, width: 32, height: 32, p: 0, color: theme.palette.blue.main }}><ArrowBackIosNew fontSize='small' /></Button>
                        <Typography variant='heavy' sx={{ fontSize: '16px' }}>Item Replacement Details</Typography>
                    </Stack>
                }
                <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <AnimatePresence initial={false} exitBeforeEnter>
                        <motion.div
                            key={replacement?.item?.id}
                            variants={replacementTransition}
                            initial='enter'
                            animate='center'
                            exit='exit'
                            transition={{
                                x: { type: 'spring', stiffness: 300, damping: 30 },
                                opacity: { duration: 0.2 },
                                duration: 0.3
                            }}
                            style={{ minHeight: '100%' }}
                        >
                            { isDeclining ?
                                <ComplianceDeclineFlow
                                    currentSwap={declineGroup}
                                    declineSwap={handleDeclineFlowSubmit}
                                    setIsDeclining={setIsDeclining}
                                    variant={pendingAction === 'accept' ? 'replacementPartialAccept' : 'replacementDecline'}
                                /> :
                                <>
                                    <Container maxWidth={false} disableGutters sx={{ display: 'flex', flex: 1, width: '100%', px: { xs: 1.5, sm: 2 }, pt: 2, pb: 2 }}>
                                        <Stack spacing={2.25} width='100%'>
                                            {reviewStatus && (
                                                <Stack role='status' aria-live='polite' direction='row' alignItems='center' spacing={1} sx={{ px: 1.5, py: 1, borderRadius: 1, color: reviewStatus.color, backgroundColor: theme.palette.background.white, borderLeft: `4px solid ${reviewStatus.color}`, boxShadow: 1 }}>
                                                    {reviewStatus.icon}
                                                    <Typography variant='heavy' sx={{ fontSize: '14px' }}>{reviewStatus.label}</Typography>
                                                </Stack>
                                            )}
                                            <ItemReplacementComparison
                                                currentItem={currentItem}
                                                proposedItem={proposedItem}
                                                itemStats={{
                                                    current: {
                                                        caseCount: replacement?.case_count_current_item ?? 0,
                                                        annualSpend: replacement?.case_item_spend_current_item ?? 0
                                                    },
                                                    recommended: {
                                                        caseCount: replacement?.case_count_recommended_item ?? 0,
                                                        annualSpend: replacement?.case_item_spend_recommended_item ?? 0
                                                    }
                                                }}
                                                status={status}
                                            />
                                            <ReplacementCardsSection cards={cards} selectedIds={selectedIds} onToggleCard={toggleRecommendation} isDisabled={status !== 'open'} />
                                        </Stack>
                                    </Container>

                                </>
                            }
                        </motion.div>
                    </AnimatePresence>
                </Stack>
                {!isDeclining &&
                    <ItemReplacementActions
                        selectedCount={selectedIds.length}
                        cardCount={cards.length}
                        totalSavings={totalSavings}
                        status={status}
                        isUpdating={isUpdating}
                        onDeclineAll={() => handleReplacementAction('decline')}
                        onAcceptSelected={() => handleReplacementAction('accept')}
                        onUndo={() => handleReplacementAction('undo')}
                        navigationAction={navigationAction}
                    />
                }
            </Stack>
        </Drawer>
    );
}

export default ItemReplacementDetails;